// Sign-in, on the same contract as the server's own pages
// (server/internal/signin): the authorization code flow with PKCE in the
// browser against the public psypen-pages client, the token in
// sessionStorage under "psypen_token", and every redirect landing on
// /signin — the server's page, which finishes the exchange and returns
// the tab to whoever asked (its "back"). Both pages read the same keys,
// so a token the hub's script minted serves this app too, and the other
// way round.
//
// The pages client has no offline_access: an expired token is a new
// round trip to the AMV login, which its own session usually answers
// without asking anything.
import { issuer, pagesClientId } from "@/config";

const TOKEN_KEY = "psypen_token";
const PKCE_KEY = "psypen_pkce";

export interface StoredToken {
  access: string;
  // Milliseconds since the epoch, when it stops working.
  exp: number;
}

interface Pkce {
  verifier: string;
  state: string;
  back: string;
}

interface Endpoints {
  authorization_endpoint: string;
  token_endpoint: string;
}

function b64url(bytes: Uint8Array): string {
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

async function endpoints(): Promise<Endpoints> {
  const res = await fetch(`${issuer.replace(/\/$/, "")}/.well-known/openid-configuration`);
  if (!res.ok) throw new Error(`auth: discovery failed (${res.status})`);
  return (await res.json()) as Endpoints;
}

// The authorize URL, apart so it is testable: every parameter the AMV
// login page takes from a public client with PKCE.
export function authorizeUrl(d: Endpoints, origin: string, challenge: string, state: string): string {
  const u = new URL(d.authorization_endpoint);
  u.search = new URLSearchParams({
    client_id: pagesClientId,
    response_type: "code",
    redirect_uri: `${origin}/signin`,
    scope: "openid",
    code_challenge: challenge,
    code_challenge_method: "S256",
    state,
  }).toString();
  return u.toString();
}

export async function makeChallenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return b64url(new Uint8Array(digest));
}

// The one way a page gets a token: a stored one with life left in it.
export function token(): StoredToken | null {
  try {
    const t = JSON.parse(sessionStorage.getItem(TOKEN_KEY) || "null") as StoredToken | null;
    if (t && t.exp > Date.now() + 30_000) return t;
  } catch {
    // Unreadable storage reads as "not signed in".
  }
  return null;
}

// A token that exists but cannot be served (expired, or inside its last
// half minute), so the caller can tell "never signed in" from "signed
// in, gone stale" (both mean: sign in).
export function staleToken(): boolean {
  try {
    const t = JSON.parse(sessionStorage.getItem(TOKEN_KEY) || "null") as StoredToken | null;
    return Boolean(t) && token() === null;
  } catch {
    return false;
  }
}

// Sends the browser to the AMV login page. Back is where the tab returns
// once /signin has finished the exchange; the default is here.
export async function signIn(back = location.pathname + location.search + location.hash): Promise<void> {
  const d = await endpoints();
  const verifier = b64url(crypto.getRandomValues(new Uint8Array(48)));
  const state = b64url(crypto.getRandomValues(new Uint8Array(16)));
  try {
    sessionStorage.setItem(PKCE_KEY, JSON.stringify({ verifier, state, back } satisfies Pkce));
  } catch {
    // Storage the page cannot write: the sign-in cannot round-trip, so
    // the tab is sent anyway and the answer is refused there — the
    // same quiet way the server's own script behaves.
  }
  location.assign(authorizeUrl(d, location.origin, await makeChallenge(verifier), state));
}

// Drops what this tab holds; the next token() asks for a fresh sign-in.
export function signOut(): void {
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(PKCE_KEY);
}

// A PKCE pair this tab started, for the tests and for /signin's page.
export function pkce(): Pkce | null {
  try {
    return JSON.parse(sessionStorage.getItem(PKCE_KEY) || "null") as Pkce | null;
  } catch {
    return null;
  }
}
